import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getHttpClient, useHttpClient } from '@bonusly/http-client';

import { TeamRequestListSchema, TeamRequestSchema } from '../schemas/team-requests';

enum QueryKey {
	TeamRequests = 'team-requests',
	MyTeamRequests = 'team-requests:mine',
	PendingConfirmations = 'team-requests:pending-confirmations',
}

const headers = { 'Key-Inflection': 'camel' };

export const teamRequestsQueryKey = (params?: Record<string, unknown>) => [QueryKey.TeamRequests, params].filter(Boolean);
export const myTeamRequestsQueryKey = () => [QueryKey.MyTeamRequests];
export const pendingConfirmationsQueryKey = () => [QueryKey.PendingConfirmations];
const teamRequestsPath = () => '/team_requests';

// Admin: list pending requests
const createFetchTeamRequests = (params?: { state?: string; page?: number; per_page?: number }) => async () => {
	const http = await getHttpClient();
	const res = await http.get(teamRequestsPath(), { params, headers });
	return TeamRequestListSchema.parse(res.data);
};

export const useTeamRequests = (params?: { state?: string; page?: number; per_page?: number }) => {
	return useQuery({
		queryKey: teamRequestsQueryKey(params),
		queryFn: createFetchTeamRequests(params),
	});
};

// Manager: list my own requests
const createFetchMyTeamRequests = () => async () => {
	const http = await getHttpClient();
	const res = await http.get(`${teamRequestsPath()}/mine`, { headers });
	return TeamRequestListSchema.parse(res.data);
};

export const useMyTeamRequests = () => {
	return useQuery({
		queryKey: myTeamRequestsQueryKey(),
		queryFn: createFetchMyTeamRequests(),
	});
};

// Manager: list requests awaiting my confirmation as new manager
const createFetchPendingConfirmations = () => async () => {
	const http = await getHttpClient();
	const res = await http.get(`${teamRequestsPath()}/pending_confirmations`, { headers });
	return TeamRequestListSchema.parse(res.data);
};

export const usePendingConfirmations = ({ enabled = true }: { enabled?: boolean } = {}) => {
	return useQuery({
		queryKey: pendingConfirmationsQueryKey(),
		queryFn: createFetchPendingConfirmations(),
		enabled,
	});
};

// Mutations

type CreateTeamRequestPayload = {
	changes: Array<{
		changeType: 'add_report' | 'remove_report' | 'change_manager';
		targetUserId?: string;
		newManagerId?: string;
	}>;
};

export const useCreateTeamRequest = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (payload: CreateTeamRequestPayload) => {
			const res = await http.post(teamRequestsPath(), { teamRequest: payload }, { headers });
			return TeamRequestSchema.parse(res.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: myTeamRequestsQueryKey() });
		},
	});
};

export const useUpdateTeamRequest = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, ...payload }: CreateTeamRequestPayload & { id: string }) => {
			const res = await http.patch(`${teamRequestsPath()}/${id}`, { teamRequest: payload }, { headers });
			return TeamRequestSchema.parse(res.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: myTeamRequestsQueryKey() });
			queryClient.invalidateQueries({ queryKey: teamRequestsQueryKey() });
		},
	});
};

export const useApproveTeamRequest = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string) => {
			const res = await http.patch(`${teamRequestsPath()}/${id}/approve`, {}, { headers });
			return TeamRequestSchema.parse(res.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: teamRequestsQueryKey() });
		},
	});
};

export const useDeclineTeamRequest = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
			const res = await http.patch(`${teamRequestsPath()}/${id}/decline`, { notes }, { headers });
			return TeamRequestSchema.parse(res.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: teamRequestsQueryKey() });
		},
	});
};

export const useConfirmTeamRequestChange = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, changeId, confirmed }: { id: string; changeId: string; confirmed: boolean }) => {
			const res = await http.patch(`${teamRequestsPath()}/${id}/confirm`, { changeId, confirmed }, { headers });
			return TeamRequestSchema.parse(res.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: teamRequestsQueryKey() });
			queryClient.invalidateQueries({ queryKey: pendingConfirmationsQueryKey() });
			queryClient.invalidateQueries({ queryKey: myTeamRequestsQueryKey() });
		},
	});
};

export const useCancelTeamRequest = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string) => {
			await http.delete(`${teamRequestsPath()}/${id}`, { headers });
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: myTeamRequestsQueryKey() });
		},
	});
};
